import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup, Button, Table, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { StepComponentProps } from "../types/proposalForm.types";

interface InsurancePlan {
  id: string;
  planname: string;
  policy_term: string;
  premium_term: string;
  payment_mode: string;
  sum_asured: string;
  premium_amount: string;
  topup_amount: string;
  double_accident_befit: string;
  dateofcommencement: string;
}

const Step5InsuranceHistory: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const [plans, setPlans] = useState<InsurancePlan[]>(
    values.insurance_plans && Array.isArray(values.insurance_plans) 
      ? values.insurance_plans 
      : []
  );
  useEffect(() => {
    if (values.insurance_plans && Array.isArray(values.insurance_plans)) {
      setPlans(values.insurance_plans);
    }
  }, [values.insurance_plans]);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<InsurancePlan>>({
    planname: "",
    policy_term: "",
    premium_term: "",
    payment_mode: "",
    sum_asured: "",
    premium_amount: "",
    topup_amount: "",
    double_accident_befit: "",
    dateofcommencement: "",
  });

  /** Restrict to numbers only: max 12 digits before decimal, max 3 after */
  const formatDecimalInput = (
    value: string,
    maxIntegerDigits = 12,
    maxDecimalDigits = 3
  ): string => {
    const stripped = value.replace(/[^\d.]/g, "");
    const parts = stripped.split(".");
    const intPart = (parts[0] || "").slice(0, maxIntegerDigits);
    const hasDot = parts.length > 1;
    const decPart = hasDot
      ? (parts.slice(1).join("") || "").slice(0, maxDecimalDigits)
      : "";
    return hasDot ? `${intPart}.${decPart}` : intPart;
  };

  const renderInput = (
    name: keyof typeof values,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderSelect = (
    name: keyof typeof values,
    label: string,
    options: Array<{ value: string; label: string }>,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="select"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        className="form-control"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderTextarea = (
    name: keyof typeof values,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="textarea"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
        rows={4}
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderDateInput = (
    name: keyof typeof values,
    label: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="date"
          name={name as string}
          value={String(values[name] || "")}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!(touched[name] && errors[name])}
          className="form-control"
          id={`date-${name}`}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={() => {
            const input = document.getElementById(`date-${name}`) as HTMLInputElement;
            if (input) {
              if (input.showPicker) {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const openModal = () => {
    setFormData({
      planname: "",
      policy_term: "",
      premium_term: "",
      payment_mode: "",
      sum_asured: "",
      premium_amount: "",
      topup_amount: "",
      double_accident_befit: "",
      dateofcommencement: "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      planname: "",
      policy_term: "",
      premium_term: "",
      payment_mode: "",
      sum_asured: "",
      premium_amount: "",
      topup_amount: "",
      double_accident_befit: "",
      dateofcommencement: "",
    });
  };

  const handleFormSubmit = () => {
    const newPlan: InsurancePlan = {
      id: Date.now().toString(),
      planname: formData.planname || "",
      policy_term: formData.policy_term || "",
      premium_term: formData.premium_term || "",
      payment_mode: formData.payment_mode || "",
      sum_asured: formData.sum_asured || "",
      premium_amount: formData.premium_amount || "",
      topup_amount: formData.topup_amount || "",
      double_accident_befit: formData.double_accident_befit || "",
      dateofcommencement: formData.dateofcommencement || "",
    };
    const updatedPlans = [...plans, newPlan];
    setPlans(updatedPlans);
    setFieldValue("insurance_plans", updatedPlans);
    closeModal();
  };

  const handleFormChange = (field: keyof InsurancePlan, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updatePlan = (id: string, field: keyof InsurancePlan, value: string) => {
    const updatedPlans = plans.map((plan) =>
      plan.id === id ? { ...plan, [field]: value } : plan
    );
    setPlans(updatedPlans);
    setFieldValue("insurance_plans", updatedPlans);
  };

  const deletePlan = (id: string) => {
    const updatedPlans = plans.filter((plan) => plan.id !== id);
    setPlans(updatedPlans);
    setFieldValue("insurance_plans", updatedPlans);
  };

  const closeRelativesPosition = String(values.close_relatives_position || "");
  const bankAccountDetails = String(values.bank_account_details || "");

  return (
    <>
      {/* Combined Section: Insurance Plans, Additional Rides, and Close Relatives */}
      <Card className="form-card shadow-sm border-0">
        <CardBody>
          <h5 className="form-title">Insurance & Background Information</h5>
          <p className="form-subtitle">
            Please provide details about your existing insurance coverage and history.
          </p>

          {/* Section 1: Insurance Plans Table */}
          <div className="section-header">
            <h6 className="section-title">Particulars of the Plan/s Proposed for Life Insurance</h6>
            <Button
              type="button"
              className="add-plan-btn add-plan-icon-btn"
              onClick={openModal}
            >
              <i className="ri-add-line"></i>
            </Button>
          </div>

          {plans.length > 0 ? (
            <div className="table-responsive">
              <Table bordered className="insurance-plans-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Term (Years)</th>
                    <th>Premium paying term</th>
                    <th>Mode: Yearly/ Half yearly/ Quarterly/ monthly/ S.P.</th>
                    <th>Sum Assured USD ($)</th>
                    <th>Premium USD ($)</th>
                    <th>Top Up Premium USD ($)</th>
                    <th>Double Accident benefit required (please state the Sum Assured)</th>
                    <th>Date of commencement (if back darting required)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            value={plan.planname}
                            onChange={(e) => updatePlan(plan.id, "planname", e.target.value)}
                            className="form-control form-control-sm"
                            placeholder="Type here"
                          />
                        ) : (
                          plan.planname || "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={plan.policy_term}
                            onChange={(e) => updatePlan(plan.id, "policy_term", formatDecimalInput(e.target.value))}
                            className="form-control form-control-sm"
                            placeholder="Years"
                          />
                        ) : (
                          plan.policy_term || "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={plan.premium_term}
                            onChange={(e) => updatePlan(plan.id, "premium_term", formatDecimalInput(e.target.value))}
                            className="form-control form-control-sm"
                            placeholder="Years"
                          />
                        ) : (
                          plan.premium_term || "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            value={plan.payment_mode}
                            onChange={(e) => updatePlan(plan.id, "payment_mode", e.target.value)}
                            className="form-control form-control-sm"
                            placeholder="e.g. Yearly, Monthly"
                          />
                        ) : (
                          plan.payment_mode || "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={plan.sum_asured}
                            onChange={(e) => updatePlan(plan.id, "sum_asured", formatDecimalInput(e.target.value))}
                            className="form-control form-control-sm"
                            placeholder="$"
                          />
                        ) : (
                          plan.sum_asured ? `$ ${plan.sum_asured}` : "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={plan.premium_amount}
                            onChange={(e) => updatePlan(plan.id, "premium_amount", formatDecimalInput(e.target.value))}
                            className="form-control form-control-sm"
                            placeholder="$"
                          />
                        ) : (
                          plan.premium_amount ? `$ ${plan.premium_amount}` : "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={plan.topup_amount}
                            onChange={(e) => updatePlan(plan.id, "topup_amount", formatDecimalInput(e.target.value))}
                            className="form-control form-control-sm"
                            placeholder="N/A"
                          />
                        ) : (
                          plan.topup_amount || "N/A"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="text"
                            value={plan.double_accident_befit}
                            onChange={(e) => updatePlan(plan.id, "double_accident_befit", e.target.value)}
                            className="form-control form-control-sm"
                            placeholder="Sum Assured or details"
                          />
                        ) : (
                          plan.double_accident_befit || "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <Input
                            type="date"
                            value={plan.dateofcommencement}
                            onChange={(e) => updatePlan(plan.id, "dateofcommencement", e.target.value)}
                            className="form-control form-control-sm"
                          />
                        ) : (
                          plan.dateofcommencement 
                            ? new Date(plan.dateofcommencement).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : "-"
                        )}
                      </td>
                      <td>
                        {editingPlanId === plan.id ? (
                          <div className="table-actions">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => setEditingPlanId(null)}
                              className="table-action-btn save-btn"
                            >
                              <i className="ri-check-line"></i>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => deletePlan(plan.id)}
                              className="table-action-btn delete-btn"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setEditingPlanId(plan.id)}
                            className="table-action-btn edit-btn"
                          >
                            <i className="ri-check-line"></i>
                            <span>Edit</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="empty-state-text">
              No plans added yet. Click the + button to add a plan.
            </p>
          )}

          {/* Section 2: Additional Insurance Rides */}
          <div className="section-spacing">
            <h6 className="section-subtitle">For additional insurance rides, if opted:</h6>

          <Row>
            <Col md={6}>
                <div className="gender-section">
                  <Label className="gender-label">
                    (A) Premium Wavier benefit required
                  </Label>
                  <div className="gender-options">
                    {(["Yes", "No"] as const).map((option) => (
                      <div key={option} className="gender-option">
                        <input
                          type="radio"
                          id={`premium_waiver-${option}`}
                          name="premium_waiver_benefit"
                          value={option}
                          checked={String(values.premium_waiver_benefit || "") === option}
                          onChange={(e) => setFieldValue("premium_waiver_benefit", e.target.value)}
                          className="gender-radio"
                        />
                        <label htmlFor={`premium_waiver-${option}`} className="gender-label-option">
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
            </Col>
            <Col md={6}>
                <div className="gender-section">
                  <Label className="gender-label">
                    (B) Family Protection benefit required
                  </Label>
                  <div className="gender-options">
                    {(["Yes", "No"] as const).map((option) => (
                      <div key={option} className="gender-option">
                        <input
                          type="radio"
                          id={`family_protection-${option}`}
                          name="family_protection_benefit"
                          value={option}
                          checked={String(values.family_protection_benefit || "") === option}
                          onChange={(e) => setFieldValue("family_protection_benefit", e.target.value)}
                          className="gender-radio"
                        />
                        <label htmlFor={`family_protection-${option}`} className="gender-label-option">
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
            </Col>
            </Row>
          </div>

          {/* Section 3: Close Relatives' Positions */}
          <div className="section-spacing">
            <h6 className="section-subtitle-sm">Close Relatives' Positions</h6>
            
            <FormGroup className="form-group">
              <Label className="label-custom">
                Is any of your close relatives - parents, spouse, children, brothers, sisters are holding any government judiciary or Military position or holding a political position? If yes please provide details-
              </Label>
              <div className="gender-options gender-options-row">
                {(["Yes", "No"] as const).map((option) => (
                  <div key={option} className="gender-option gender-option-row">
                    <input
                      type="radio"
                      id={`close_relatives-${option}`}
                      name="close_relatives_position"
                      value={option}
                      checked={closeRelativesPosition === option}
                      onChange={(e) => setFieldValue("close_relatives_position", e.target.value)}
                      className="gender-radio"
                    />
                    <label htmlFor={`close_relatives-${option}`} className="gender-label-option gender-label-row">
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </FormGroup>

            {closeRelativesPosition === "Yes" && (
              <Row className="section-spacing-sm">
            <Col md={4}>
                  {renderInput("relative_name", "Name", "text", "Type here", false)}
            </Col>
            <Col md={4}>
              {renderInput("relative_relationship", "Relationship", "text", "Type here", false)}
            </Col>
            <Col md={4}>
              {renderInput("relative_position", "Position held", "text", "Type here", false)}
            </Col>
          </Row>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Section 4: Bank Account Details */}
      <Card className="form-card shadow-sm border-0 card-spacing">
        <CardBody>
          <h5 className="form-title">Bank Account Details</h5>
          <p className="form-subtitle">
            Please provide bank account details, which you will be using to pay the premium. Payment of premium by way of cheque and telex transfers must be in favor of LIC international only
          </p>

          <div className="gender-section" style={{ marginTop: "1rem" }}>
            <Label className="gender-label">Bank Account Details</Label>
            <div className="gender-options">
              {(["Yes", "No"] as const).map((option) => (
                <div key={option} className="gender-option">
                  <input
                    type="radio"
                    id={`bank_account-${option}`}
                    name="bank_account_details"
                    value={option}
                    checked={bankAccountDetails === option}
                    onChange={(e) => setFieldValue("bank_account_details", e.target.value)}
                    className="gender-radio"
                  />
                  <label htmlFor={`bank_account-${option}`} className="gender-label-option">
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {bankAccountDetails === "Yes" && (
            <Row className="section-spacing-sm">
              <Col md={6}>
                {renderInput("bank_name", "Name of the Bank", "text", "Type here", true)}
              </Col>
              <Col md={6}>
                {renderInput("bank_branch", "Bank Branch", "text", "Type here", true)}
              </Col>
              <Col md={6}>
                {renderInput("iban", "IBAN", "text", "Type here", true)}
              </Col>
              <Col md={6}>
                {renderInput("account_number", "Account Number", "text", "Type here", true)}
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Section 5: Question for UAE customers only */}
      <Card className="form-card shadow-sm border-0 card-spacing">
        <CardBody>
          <h5 className="form-title">Question for UAE customers only</h5>
          <p className="form-subtitle subtitle-spacing-sm">
            Please provide details of banks you are dealing with/ holding account with. Please provide bank name and type of account maintained. (current/savings)
          </p>
          <p className="form-subtitle subtitle-spacing">
            In case no personal account is maintained and all transactions are being made from company account details of company accounts should be provided.
          </p>

          <Row>
            <Col md={12}>
              {renderTextarea(
                "uae_bank_details",
                "",
                "Type here",
                false
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Policy Details Modal */}
      <Modal isOpen={isModalOpen} toggle={closeModal} className="policy-details-modal">
        <ModalHeader toggle={closeModal} className="policy-modal-header">
          Policy Details
        </ModalHeader>
        <ModalBody className="policy-modal-body">
          <Row className="policy-modal-row">
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Plan</Label>
                <Input
                  type="text"
                  value={formData.planname || ""}
                  onChange={(e) => handleFormChange("planname", e.target.value)}
                  placeholder="Type here"
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Term (Years)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.policy_term || ""}
                  onChange={(e) => handleFormChange("policy_term", formatDecimalInput(e.target.value))}
                  className="form-control"
                  placeholder="Numbers only, e.g. 10 or 10.5"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Premium Paying Term</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.premium_term || ""}
                  onChange={(e) => handleFormChange("premium_term", formatDecimalInput(e.target.value))}
                  className="form-control"
                  placeholder="Numbers only, e.g. 10 or 10.5"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Payment Mode</Label>
                <Input
                  type="text"
                  value={formData.payment_mode || ""}
                  onChange={(e) => handleFormChange("payment_mode", e.target.value)}
                  className="form-control"
                  placeholder="e.g. Yearly, Half yearly, Quarterly, Monthly, S.P."
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Top Up Amount ($)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.topup_amount || ""}
                  onChange={(e) => handleFormChange("topup_amount", formatDecimalInput(e.target.value))}
                  placeholder="Numbers only, max 12 digits, 3 decimals"
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Sum Assured</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.sum_asured || ""}
                  onChange={(e) => handleFormChange("sum_asured", formatDecimalInput(e.target.value))}
                  placeholder="Numbers only, max 12 digits, 3 decimals"
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Premium Amount ($)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.premium_amount || ""}
                  onChange={(e) => handleFormChange("premium_amount", formatDecimalInput(e.target.value))}
                  placeholder="Numbers only, max 12 digits, 3 decimals"
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Double Accident benefit</Label>
                <Input
                  type="text"
                  value={formData.double_accident_befit || ""}
                  onChange={(e) => handleFormChange("double_accident_befit", e.target.value)}
                  placeholder="Type here"
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="form-group">
                <Label>Date of commencement</Label>
                <Input
                  type="date"
                  value={formData.dateofcommencement || ""}
                  onChange={(e) => handleFormChange("dateofcommencement", e.target.value)}
                  className="form-control"
                  id="modal-date-commencement"
                />
          </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="policy-modal-footer">
          <Button
            type="button"
            onClick={handleFormSubmit}
            className="policy-continue-btn"
            style={{
              background: "linear-gradient(90deg, #26547C 0%, #45AB8B 100%)",
              border: "none",
              padding: "0.5rem 1.5rem",
              color: "#ffffff",
              borderRadius: "6px",
            }}
          >
            Continue
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Step5InsuranceHistory;
